import React, { useEffect, useMemo, useState } from "react";
import { Flex } from "@chakra-ui/react";
import { BasicBlock, DragKind, ItemStatus, RecurringOption } from "../../../../types/block/enum";
import { pxToRem } from "../../../../utils/styles/size";
import TaskListEmptyDropArea from "../../TaskListEmptyDropArea";
import TaskDividerBlock from "../Divider/TaskDividerBlock";
import TaskRow from "../InboxBlock/TaskRow";
import { BlockType, DragBlockType } from "../../../../types/block/type";
import { useInboxGeneralQuery } from "../../../../react-query/inbox/queries";
import { useRecoilState, useRecoilValue } from "recoil";
import { draggedEventState } from "../../../../recoil/calendar/calendarStateV2";
import useHandleClientBlockStateDelete from "../../../../hooks/block/useHandleClientBlockStateDelete";
import { juneTrack } from "../../../../utils/june/analytics";
import { useUpdateBlockMutation } from "../../../../react-query/block/useUpdateBlockMutation";
import {
  inProgressAndCompletedSpaceListState,
  spaceTypesSelector,
} from "../../../../recoil/spaces/inProgressSpaceListState";
import { SpaceKind } from "../../../../types/space/enum";
import { usePreference } from "../../../../hooks/preference/usePreference";
import { getBlockTypeFlags } from "../../../../utils/common/block/getBlockTypeFlags";
import { getAllSpaceIds } from "../../../../services/space/space.service";
import { removeDragOnlyFields } from "../../../../utils/common/block/removeClientOnlyFields";
import { inboxDraggableListState } from "../../../../recoil/dnd/dragState";

export interface InboxGeneralProps {
  taskSelectedId: number | null;
  handleSaveTaskSelectedId: (id: number | null) => void;
  handleDeleteDivider: (selectedDividerId: number) => void;
  handleDuplicateInboxBlock: (e: React.MouseEvent, data: BlockType) => void;
  handleReorder: (ids: number[]) => void;
  handleClickInboxBlock: (
    e: React.MouseEvent,
    data: BlockType,
    ref: React.RefObject<HTMLDivElement>
  ) => void;
  handleDataDelete: (data: BlockType) => void;
  onClickCheckBox: (e: React.MouseEvent, block: BlockType) => void;
  flexGrow: number;
  hasBlocksSynchronizedWithGoogle: boolean;
}

function InboxGeneral({
  taskSelectedId,
  handleSaveTaskSelectedId,
  handleDeleteDivider,
  handleDuplicateInboxBlock,
  handleReorder,
  handleClickInboxBlock,
  handleDataDelete,
  onClickCheckBox,
  flexGrow,
  hasBlocksSynchronizedWithGoogle,
}: InboxGeneralProps) {
  const [inboxDraggableList, setInboxDraggableList] = useRecoilState(inboxDraggableListState);
  const inProgressAndCompletedSpaceList = useRecoilValue(inProgressAndCompletedSpaceListState);
  const draggedEvent = useRecoilValue(draggedEventState);
  const [isDraggingToInbox, setIsDraggingToInbox] = useState<boolean>(false);
  const useUpdateBlock = useUpdateBlockMutation();

  const { preference } = usePreference();
  const inProgressSpaceListWithoutDivider = useRecoilValue(
    spaceTypesSelector([SpaceKind.Default, SpaceKind.Normal])
  );

  // 캘린더 클라이언트 상태 업데이트 훅
  const { handleClientBlockDelete } = useHandleClientBlockStateDelete();

  const inboxGeneralQuery = useInboxGeneralQuery({
    enabled: hasBlocksSynchronizedWithGoogle,
  });

  const isSpaceFilterAll = useMemo(() => {
    const selectedSpaceNumber = preference.selectedSpaceIds?.length ?? 0;
    const inProgressSpaceNumber = inProgressSpaceListWithoutDivider?.length ?? 0;

    return selectedSpaceNumber === inProgressSpaceNumber;
  }, [inProgressSpaceListWithoutDivider.length, preference?.selectedSpaceIds?.length]);

  useEffect(
    function initializeInboxDraggableList() {
      if (!inboxGeneralQuery.data) return;

      const filteredInboxGeneralList = inboxGeneralQuery.data.filter((block) => {
        const { isDivider } = getBlockTypeFlags(block.blockType);
        if (isDivider) return true;

        // 기획: 모든 스페이스를 선택한 경우 아카이브 스페이스를 포함한 블록을 보여줌
        const currentSpaceList = isSpaceFilterAll
          ? getAllSpaceIds(inProgressAndCompletedSpaceList)
          : preference.selectedSpaceIds;

        return block?.spaceId && currentSpaceList.includes(block.spaceId);
      });

      setInboxDraggableList(filteredInboxGeneralList);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      inboxGeneralQuery.data,
      isSpaceFilterAll,
      preference.selectedSpaceIds,
      inProgressAndCompletedSpaceList,
    ]
  );

  const handleUpdateCalendarToInbox = async (block: DragBlockType) => {
    const prevBlockData = removeDragOnlyFields(block);
    const { hangoutLink, meetingCode, linkData, attendees, location, priority } = prevBlockData;

    const isRecurringBlock =
      (prevBlockData.recurrence && prevBlockData.recurrence?.length > 0) ||
      Boolean(prevBlockData.originalId);

    const updatePayload = {
      hangoutLink,
      meetingCode,
      linkData,
      attendees,
      location,
      priority,
      start: null,
      end: null,
      allDay: false,
      itemStatus: ItemStatus.IN_PROGRESS,
      displayOrder: 0,
      ...(isRecurringBlock && { recurrence: null }), // 반복 블록일 경우 recurrence 삭제
    };

    // 캘린더 클라이언트 상태 업데이트(inbox로 이동한 블록 삭제)
    // 반복 블록인 경우 전체 삭제
    handleClientBlockDelete({
      updateBlockData: prevBlockData,
      recurringOption: isRecurringBlock ? RecurringOption.ALL : undefined,
    });

    useUpdateBlock.mutate(
      {
        id: prevBlockData.id,
        payload: updatePayload,
        prevData: prevBlockData,
      },
      {
        onSettled: () => {
          juneTrack("move_task");
        },
      }
    );
  };

  const renderedList = useMemo(() => {
    if (!inboxGeneralQuery.data) {
      return (
        <TaskListEmptyDropArea
          onTasksReorderChange={handleReorder}
          handleUpdateTaskList={handleDataDelete}
          listType={DragKind.INBOX_TASK}
        />
      );
    }

    return inboxDraggableList.map((taskDataRow, index) => {
      if (taskDataRow.blockType === BasicBlock.DIVIDER) {
        if (!isSpaceFilterAll) return null;

        return (
          <TaskDividerBlock
            key={taskDataRow.clientId ?? taskDataRow.id}
            index={index}
            data={taskDataRow}
            isSelected={taskSelectedId === taskDataRow.id}
            onDelete={() => handleDeleteDivider(taskDataRow.id)}
            onDuplicate={(e) => handleDuplicateInboxBlock(e, taskDataRow)}
            onReorder={handleReorder}
            onSelect={handleSaveTaskSelectedId}
          />
        );
      }

      return (
        <TaskRow
          key={taskDataRow.clientId ?? taskDataRow.id}
          index={index}
          data={taskDataRow}
          isSelected={taskSelectedId === taskDataRow.id}
          onDataDuplicate={handleDuplicateInboxBlock}
          onSelect={handleSaveTaskSelectedId}
          onDataDelete={handleDataDelete}
          isOverdue={false}
          onReorder={handleReorder}
          onClickBlock={handleClickInboxBlock}
          onClickCheckBox={onClickCheckBox}
        />
      );
    });
  }, [
    inboxGeneralQuery.data,
    inboxDraggableList,
    taskSelectedId,
    handleDeleteDivider,
    handleDuplicateInboxBlock,
    handleReorder,
    handleSaveTaskSelectedId,
    handleClickInboxBlock,
    handleDataDelete,
    onClickCheckBox,
    isSpaceFilterAll,
  ]);

  return (
    <Flex
      flexGrow={flexGrow}
      gap={pxToRem(8)}
      id="memoizedTasklist"
      borderRadius={"6px"}
      flexDirection="column"
      height="fit-content"
      border={"2px solid"}
      borderColor={isDraggingToInbox ? "whiteAlpha.400" : "transparent"}
      onMouseEnter={() => {
        if (draggedEvent && draggedEvent?.dragType === DragKind.CALENDAR_TASK) {
          setIsDraggingToInbox(true);
        }
      }}
      onMouseLeave={() => {
        setIsDraggingToInbox(false);
      }}
      onMouseUp={() => {
        if (draggedEvent?.dragType === DragKind.CALENDAR_TASK) {
          handleUpdateCalendarToInbox(draggedEvent);
          setIsDraggingToInbox(false);
        }
      }}
    >
      {renderedList}
    </Flex>
  );
}

export default InboxGeneral;
